<template>
   <div>
      <SnInputLabel
         :formControlId="id"
         :labelText="label"
         :labelSup="labelSup"
         :descriptionText="description"
         :indicateRequired="indicateRequired"
         :icon="labelIcon"
         :toolTipText="labelTooltip"
         :showLabel="showLabel"
         :showDescription="showDescription"
      />

      <div class="form-control-wrapper">
         <SnIcon name="search" size="small" class="search-icon" data-test="search-icon" v-if="type === 'search'" />
         <input
            :id="id"
            v-model="model"
            :autocomplete="!!autocomplete ? 'on' : 'off'"
            :data-test="id"
            :name="id"
            class="form-control"
            v-bind="{ maxLength, type, placeholder, disabled }"
            :class="[
               inputClass,
               { 'has-icon': !!inputIcon },
               { 'has-label': showLabel && label },
               { 'red-border-error': redBorderError && !!error },
               { 'input-large': size === 'large' },
            ]"
            :aria-label="label"
            :aria-invalid="error ? 'true' : 'false'"
            :aria-describedby="ariaDescribeBy"
         />
         <SnIcon
            v-if="inputIcon"
            :name="inputIcon"
            size="small"
            class="form-control-icon"
            data-test="form-control-icon"
         />
      </div>
      <p
         v-if="hasReachedMaxLength && maxLengthText"
         :id="`${id}-character-max-message`"
         class="character-length invalid"
         data-test="character-limit-reached"
      >
         {{ maxLengthText }}
      </p>
      <p
         v-else-if="showCharacterCount"
         :id="`${id}-character-count-message`"
         class="character-length"
         data-test="character-count"
      >
         {{ characterCountText || `${model?.length}/${maxLength}` }}
      </p>

      <SnAlert
         v-if="error"
         :message="error"
         :id="`${id}-validation-message`"
         theme="danger"
         size="small"
         class="validation-alert"
         :icon="iconError ? 'warning-circle-filled' : undefined"
      />
   </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import SnIcon, { type Icon } from "../../SnIcon/SnIcon.vue";
import SnInputLabel from "../../Form/SnInputLabel/SnInputLabel.vue";
import SnAlert from "../../SnAlert/SnAlert.vue";

/** Props **/
interface Props {
   id: string;
   label: string;
   labelSup?: string;
   disabled?: boolean;
   showLabel?: boolean;
   description?: string;
   showDescription?: boolean;
   indicateRequired?: boolean;
   labelIcon?: Icon;
   labelTooltip?: string;
   autocomplete?: boolean;
   maxLength?: number;
   maxLengthText?: string;
   type?: string;
   placeholder?: string;
   inputIcon?: Icon;
   inputClass?: string | object;
   showCharacterCount?: boolean;
   characterCountText?: string;
   error?: string;
   redBorderError?: boolean;
   iconError?: boolean;
   size?: string;
}

const props = withDefaults(defineProps<Props>(), {
   type: "text",
   showLabel: true,
   showDescription: true,
   redBorderError: false,
   iconError: false,
   size: "standard",
});

/** Model **/
const model = defineModel<string>("modelValue");

/** Computed **/
const hasReachedMaxLength = computed(() => {
   return props.maxLength && model.value && model.value?.length >= props.maxLength;
});

const ariaDescribeBy = computed(() => {
   const res = [];
   if (props.description) {
      res.push(`${props.id}-label-description`);
   }
   if (props.error) {
      res.push(`${props.id}-validation-message`);
   }
   if (hasReachedMaxLength.value && props.maxLengthText) {
      res.push(`${props.id}-character-max-message`);
   } else if (props.showCharacterCount && props.characterCountText) {
      res.push(`${props.id}-character-count-message`);
   }
   return res.join(" ");
});
</script>

<style scoped lang="scss">
.character-length {
   margin-block-start: var(--size-base-4);
   font-size: var(--size-text-small);
   color: var(--colour-text-accent);
   text-align: right;

   &.invalid {
      color: var(--colour-danger-default);
   }
}

.validation-alert {
   margin-block-start: var(--size-base-8);
}

.form-control.input-large {
   height: var(--size-base-48);
}
</style>
